import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { Check, ArrowRight, Plus, Minus } from "lucide-react";
import { Seo } from "@/components/Seo";
import { Reveal } from "@/components/Reveal";
import { BeforeAfter } from "@/components/BeforeAfter";
import { IMG } from "@/constants/images";
import { waLink } from "@/constants/site";

const PROCESS = ["Assessment", "Surface Prep", "Application", "Curing", "Quality Check"];

export const APPLICATIONS = {
  "sofa-protection": {
    tag: "NANO", hero: IMG.sofa, eyebrow: "Sofa & Upholstery Protection",
    title: "Live freely. Protect beautifully.",
    intro: "Advanced nano coating for sofas, upholstery, fabric chairs, cushions and luxury furniture — repelling spills and resisting stains without changing how your furniture looks or feels.",
    protects: ["Coffee & tea", "Wine & juice", "Oil & food", "Dust", "Pet stains", "Everyday spills"],
    benefits: ["Water Repellent", "Stain Resistant", "Dust Resistant", "UV Protection", "Easy Cleaning", "Fabric Feel Preserved"],
    materials: ["Fabric", "Velvet", "Linen", "Cotton", "Leather", "Suede"],
    cta: "BOOK SOFA PROTECTION",
    faqs: [["Will it change the feel of my sofa?", "No — the nano coating is breathable and preserves the original texture and feel."], ["Is it safe for pets and children?", "Once cured, the coating is designed to be safe for everyday home use."]],
  },
  "curtain-protection": {
    tag: "NANO", hero: IMG.curtains, eyebrow: "Curtain Protection",
    title: "Keep your curtains beautiful for longer.",
    intro: "Nano protection for sheer, linen, velvet, blackout and designer curtains — guarding against water, stains and dust while maintaining the natural drape and feel.",
    protects: ["Water marks", "Stains", "Dust", "Sun fading", "Everyday handling", "Humidity"],
    benefits: ["Water Repellent", "Stain Protection", "Dust Resistance", "UV Protection", "Easy Maintenance", "Fabric Feel Preserved"],
    materials: ["Sheer", "Linen", "Velvet", "Blackout", "Cotton", "Designer Fabric"],
    cta: "BOOK CURTAIN PROTECTION",
    faqs: [["Can it be applied on installed curtains?", "Yes, in most cases our team applies protection on-site with minimal disruption."], ["Does it affect the colour?", "No, the treatment is transparent and preserves the original colour."]],
  },
  "rug-carpet-protection": {
    tag: "NANO", hero: IMG.rug, eyebrow: "Rug & Carpet Protection",
    title: "Beauty under protection.",
    intro: "Protection for luxury rugs, Persian rugs, designer rugs, carpets and hospitality flooring — helping them resist spills, stains and daily foot traffic.",
    protects: ["Liquid spills", "Stains", "Dust & dirt", "Foot traffic wear", "Fading", "Odour"],
    benefits: ["Water Repellent", "Stain Resistant", "Dust Resistant", "UV Protection", "Easy Cleaning", "Fibre Preservation"],
    materials: ["Wool", "Silk", "Persian", "Synthetic", "Hand-knotted", "Hospitality Carpet"],
    cta: "BOOK RUG PROTECTION",
    faqs: [["Is it suitable for antique rugs?", "We assess each rug individually and recommend the safest suitable treatment."], ["How long does it last?", "Longevity depends on use; our team advises a maintenance schedule at assessment."]],
  },
  "dining-table-protection": {
    tag: "SPF", hero: IMG.diningTable, eyebrow: "Dining Table Protection",
    title: "For moments that matter.",
    intro: "Surface Protection Film (SPF) for marble, wooden, glass and stone dining tables — helping guard against everyday wear, stains and scratches so your table stays beautiful through every gathering.",
    protects: ["Scratches", "Hot dishes marks", "Wine & food stains", "Water rings", "Cutlery scuffs", "Daily use"],
    benefits: ["Scratch Protection", "Stain Protection", "Heat Resistance", "UV Protection", "Easy Maintenance", "Crystal Clear Finish"],
    materials: ["Marble", "Wood", "Glass", "Granite", "Quartz", "Veneer"],
    cta: "BOOK DINING TABLE PROTECTION",
    faqs: [["Is the film visible?", "SPF is ultra-clear and designed to be virtually invisible on your table surface."], ["Can hot vessels be placed on it?", "SPF offers heat resistance for everyday use; we still recommend trivets for very hot cookware."]],
  },
  "kitchen-countertop-protection": {
    tag: "SPF", hero: IMG.kitchen, eyebrow: "Kitchen Countertop Protection",
    title: "Your kitchen works hard. Protect its surfaces.",
    intro: "Surface Protection Film for marble, granite, quartz, engineered stone and solid-surface countertops and islands — engineered to resist stains, scratches and daily kitchen wear.",
    protects: ["Scratches", "Stains", "Water & spills", "Heat marks", "Acidic etching", "Daily use"],
    benefits: ["Scratch Protection", "Stain Protection", "Water Repellency", "Heat Resistance", "Easy Maintenance", "Crystal Clear Finish"],
    materials: ["Marble", "Granite", "Quartz", "Engineered Stone", "Solid Surface", "Laminate"],
    cta: "GET A QUOTE",
    faqs: [["Is it food-safe?", "Once cured, the protection is designed to be safe for kitchen surfaces used in food areas."], ["Will it cover my whole island?", "Yes, we measure and apply protection across countertops and islands during installation."]],
  },
};

export default function ApplicationPage({ slug }) {
  const d = APPLICATIONS[slug];
  const navigate = useNavigate();
  const [openFaq, setOpenFaq] = useState(0);

  return (
    <div data-testid={`application-${slug}`}>
      <Seo title={d.eyebrow} description={`${d.title} ${d.intro}`} />
      <section className="relative min-h-[62vh] flex items-center overflow-hidden">
        <img src={d.hero} alt={d.eyebrow} className="absolute inset-0 w-full h-full object-cover" />
        <div className="absolute inset-0 bg-gradient-to-r from-black via-black/75 to-black/25" />
        <div className="relative max-w-[1500px] mx-auto px-5 lg:px-10">
          <p className="errah-label mb-3">{d.tag} · {d.eyebrow}</p>
          <h1 className="serif-head text-4xl lg:text-7xl max-w-3xl">{d.title}</h1>
          <p className="mt-5 text-gray-300 max-w-xl leading-relaxed">{d.intro}</p>
          <div className="mt-8 flex gap-3">
            <button onClick={() => navigate("/consultation", { state: { preferred_service: d.eyebrow } })} className="bg-[#C9A45C] text-black px-7 py-3.5 text-sm font-semibold hover:bg-[#E4C987] transition-colors rounded-sm">{d.cta}</button>
            <a href={waLink(`Hi ERRAH, I'm interested in ${d.eyebrow}.`)} target="_blank" rel="noopener noreferrer" className="border border-[#C9A45C]/50 px-7 py-3.5 text-sm font-semibold hover:bg-[#C9A45C] hover:text-black transition-colors rounded-sm">WhatsApp</a>
          </div>
        </div>
      </section>

      {/* Protects against + Before/After */}
      <section className="max-w-[1500px] mx-auto px-5 lg:px-10 py-14 grid lg:grid-cols-2 gap-10 items-center">
        <Reveal>
          <h2 className="serif-head text-3xl lg:text-4xl mb-6">What it protects against</h2>
          <div className="grid grid-cols-2 gap-3">
            {d.protects.map((p) => <div key={p} className="hair-border bg-warmblack px-4 py-3 text-sm text-gray-300 flex items-center gap-2"><Check size={15} className="text-[#C9A45C]" />{p}</div>)}
          </div>
        </Reveal>
        <Reveal delay={0.1}>
          <BeforeAfter className="aspect-[4/3]" beforeLabel="Unprotected" afterLabel="ERRAH Protected" />
          <p className="mt-2 text-xs text-gray-600 text-center">Illustrative — real project images can be uploaded from Admin.</p>
        </Reveal>
      </section>

      {/* Benefits */}
      <section className="bg-warmblack border-y border-[#C9A45C]/15">
        <div className="max-w-[1500px] mx-auto px-5 lg:px-10 py-8 grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-4">
          {d.benefits.map((b) => <div key={b} className="flex items-center gap-2 text-sm text-gray-300"><Check size={16} className="text-[#C9A45C] shrink-0" />{b}</div>)}
        </div>
      </section>

      {/* Process + Materials */}
      <section className="max-w-[1500px] mx-auto px-5 lg:px-10 py-14 grid lg:grid-cols-2 gap-12">
        <Reveal>
          <h2 className="serif-head text-3xl mb-6">Application process</h2>
          <div className="space-y-3">
            {PROCESS.map((s, i) => (
              <div key={s} className="flex items-center gap-4">
                <span className="w-8 h-8 shrink-0 border border-[#C9A45C]/40 text-[#C9A45C] flex items-center justify-center text-sm">{i + 1}</span>
                <span className="text-gray-300">{s}</span>
              </div>
            ))}
          </div>
        </Reveal>
        <Reveal delay={0.1}>
          <h2 className="serif-head text-3xl mb-6">Suitable materials</h2>
          <div className="flex flex-wrap gap-2">
            {d.materials.map((m) => <span key={m} className="px-4 py-2 text-sm border border-white/15 text-gray-300">{m}</span>)}
          </div>
        </Reveal>
      </section>

      {/* FAQ */}
      <section className="max-w-[900px] mx-auto px-5 lg:px-10 py-6">
        <h2 className="serif-head text-3xl mb-6">Frequently asked</h2>
        <div className="divide-y divide-white/10 hair-border">
          {d.faqs.map(([q, a], i) => (
            <div key={i} className="p-5" data-testid={`faq-${i}`}>
              <button onClick={() => setOpenFaq(openFaq === i ? -1 : i)} className="w-full flex items-center justify-between text-left">
                <span className="font-serif text-lg">{q}</span>
                {openFaq === i ? <Minus size={18} className="text-[#C9A45C]" /> : <Plus size={18} className="text-[#C9A45C]" />}
              </button>
              {openFaq === i && <p className="mt-3 text-sm text-gray-400 leading-relaxed">{a}</p>}
            </div>
          ))}
        </div>
      </section>

      <section className="max-w-[1500px] mx-auto px-5 lg:px-10 py-12">
        <div className="hair-border bg-warmblack p-10 text-center">
          <h2 className="serif-head text-4xl">Protect it the ERRAH way.</h2>
          <button onClick={() => navigate("/consultation", { state: { preferred_service: d.eyebrow } })} className="mt-6 inline-flex items-center gap-2 bg-[#C9A45C] text-black px-8 py-4 text-sm font-semibold hover:bg-[#E4C987] transition-colors rounded-sm">{d.cta} <ArrowRight size={15} /></button>
        </div>
      </section>
    </div>
  );
}
